import React from 'react';
import { Button } from '@/components/ui/button';
import { ChevronLeft, ChevronRight, MoreHorizontal } from 'lucide-react';
import { PaginationProps } from '../types';

const Pagination: React.FC<PaginationProps> = ({
  currentPage = 1,
  links = [],
  onPageChange,
  onNextPage,
  onPrevPage,
}) => {
  if (!links || !Array.isArray(links) || links.length <= 3) {
    // Don't show pagination if there's only one page or no data
    return null;
  }

  // Filter out the Previous and Next buttons from links
  const pageLinks = links.filter(link => 
    link && 
    link.label !== '&laquo; Previous' && 
    link.label !== 'Next &raquo;' &&
    !isNaN(parseInt(link.label))
  );

  const hasNextPage = links.some(link => link && link.label === 'Next &raquo;' && link.url);
  const hasPrevPage = links.some(link => link && link.label === '&laquo; Previous' && link.url);

  const renderPageNumbers = () => {
    const maxVisiblePages = 5;
    const totalPages = pageLinks.length;
    
    if (totalPages <= maxVisiblePages) {
      return pageLinks.map((link, index) => (
        <Button
          key={`page-${index}`}
          variant={link.active ? "default" : "outline"}
          size="sm"
          onClick={() => onPageChange(parseInt(link.label))}
          disabled={!link.url}
          className="h-8 w-8 p-0"
        >
          {link.label}
        </Button>
      ));
    }

    // Complex pagination with ellipsis
    const startPage = Math.max(1, currentPage - 2);
    const endPage = Math.min(totalPages, currentPage + 2);
    const pages = [];

    // First page
    if (startPage > 1) {
      pages.push(
        <Button
          key="page-1"
          variant={currentPage === 1 ? "default" : "outline"}
          size="sm"
          onClick={() => onPageChange(1)}
          className="h-8 w-8 p-0"
        >
          1
        </Button>
      );
      if (startPage > 2) {
        pages.push(
          <Button
            key="ellipsis-start"
            variant="outline"
            size="sm"
            disabled
            className="h-8 w-8 p-0"
          >
            <MoreHorizontal className="h-4 w-4" />
          </Button>
        );
      }
    }

    // Middle pages
    for (let i = startPage; i <= endPage; i++) {
      pages.push(
        <Button
          key={`page-${i}`}
          variant={currentPage === i ? "default" : "outline"}
          size="sm"
          onClick={() => onPageChange(i)}
          className="h-8 w-8 p-0"
        >
          {i}
        </Button>
      );
    }

    // Last page
    if (endPage < totalPages) {
      if (endPage < totalPages - 1) {
        pages.push(
          <Button
            key="ellipsis-end"
            variant="outline"
            size="sm"
            disabled
            className="h-8 w-8 p-0"
          >
            <MoreHorizontal className="h-4 w-4" />
          </Button>
        );
      }
      pages.push(
        <Button
          key={`page-${totalPages}`}
          variant={currentPage === totalPages ? "default" : "outline"}
          size="sm"
          onClick={() => onPageChange(totalPages)}
          className="h-8 w-8 p-0"
        >
          {totalPages}
        </Button>
      );
    }

    return pages;
  };

  return (
    <div className="flex items-center justify-center space-x-2 py-4">
      <Button
        variant="outline"
        size="sm"
        onClick={onPrevPage}
        disabled={!hasPrevPage}
        className="h-8 w-8 p-0"
      >
        <ChevronLeft className="h-4 w-4" />
      </Button>

      {renderPageNumbers()}

      <Button
        variant="outline"
        size="sm"
        onClick={onNextPage}
        disabled={!hasNextPage}
        className="h-8 w-8 p-0"
      >
        <ChevronRight className="h-4 w-4" />
      </Button>
    </div>
  );
};

export default Pagination;